// /prep: Pixally Prep, the app for Mac and Windows (beta) that preps a wedding's
// footage for the edit.
//
// One source for the page (pages/PrepPage.jsx), the prerendered HTML crawlers
// read (scripts/prerender.mjs), the sitemap, llms.txt and SEOHead. Plain JS, no
// JSX, so the build scripts can import it.
//
// Prep is its own product in its own repository (pixally-prep) and on its own
// server (prep.pixally.com). Every fact here comes from that repository: its
// decisions, and the help text inside the app (app/src/lib/help.ts), which is
// where the wording about first cuts, timelines and timing is taken from. The
// copy keeps to these rules:
//   - Highlight prep and the doc edit's first cut are the two main features,
//     equal, with highlight prep first: side by side above the download, then a
//     panel each in that order (Ryan, 11 Oct 2026). /get-prep shows the doc
//     edit first in both places (AD_PAGE below). The first cut is one to
//     polish, never a finished film, and the copy says so.
//   - A highlight film gets the prep only, never a cut, and the copy says so.
//     What the page sells for a highlight is the start it gives: the soundbites
//     and candid moments pulled aside and the whole day laid out, so the
//     filmmaker begins with the creative part (Ryan, 11 Oct 2026).
//   - About uploads the page says one thing: there is no need to upload footage
//     anywhere, because the video and audio files stay on the computer (Ryan, 11
//     Oct 2026). It never says "nothing leaves your computer", or anything else that
//     claims nothing is sent: Prep's AI steps do send transcripts and frames
//     through Pixally's server, and Prep's own privacy policy is where that is
//     set out.
//   - Prep does not write stories or storyboards. Plotline, a Founding Partner on
//     /perks, does, so this page does not claim it.
//   - The first wedding is free because that is Prep's price. It is never a
//     reward for joining the Pixally waitlist. It is one per person (an account,
//     which is one verified email), and one per computer as the backup against a
//     second email on the same computer (Ryan, 11 Oct 2026; "computer" since
//     Windows, 18 Oct 2026). Say both, never "one per computer" on its own.
//   - Prep runs on a Mac and, as a beta, on Windows (18 Oct 2026). Windows is
//     labeled beta wherever it is named, and the beta's promise is the app's and
//     Prep's terms' own words: "If a wedding comes out with real problems, tell
//     us and we'll give you the wedding back." Say "your computer", or "Mac or
//     PC", where a sentence is about any computer, and "Mac" only where it is
//     about the Mac. Final Cut Pro is Mac only, and the copy says so wherever it
//     names the editors for both.
//   - Prep's privacy policy and terms are its own, on prep.pixally.com
//     (published 11 Oct 2026). Both pages link them in the footer (PREP_LEGAL).
//
// /get-prep, the page Prep's ads link to, takes its words from here too
// (AD_PAGE below, pages/GetPrepPage.jsx).
import { LAUNCH_LABEL } from '../config/launch.js';
import { pricingPromo } from '../config/pricingPromo.js';
import { attributedDownloadHref, attributedSendLink } from '../lib/prepAttribution.js';

export const ROUTE = '/prep';

// THE RELEASE SWITCH. false: the download button is drawn but switched off
// ("Coming soon"), and nothing links to a download. true: a Mac gets the live
// button. Flip it when a release is published on Prep's admin page; until then
// prep.pixally.com/download answers 503. On since 14 Oct 2026, when 0.1.6 was
// published and the link began forwarding to its .dmg.
export const PREP_RELEASED = true;

// THE WINDOWS SWITCH. true: Windows gets its own button, "Download for Windows
// (beta)", first on a Windows PC and second everywhere else. false: the Windows
// button is not drawn, and a Windows PC gets the send-the-link button as a phone
// does. The words keep saying Mac or PC either way, so turning it off is for an
// emergency, not for hiding Windows. On since the Windows beta's release (Ryan,
// 18 Oct 2026).
export const PREP_WINDOWS_RELEASED = true;

// Prep's own counted link. It records the visit and forwards to the newest
// release's .dmg, so the site links here and never to a file. Each placement on
// pixally.com gets its own campaign, which is how Prep's records tell them apart.
//
// A visitor who came on a tagged link (an ad, a post) keeps that link's tags
// instead, so Prep's records credit the download, and the account the Mac then
// makes, to the ad or the post. Prep keeps utm_source, utm_medium and
// utm_campaign, up to 120 characters each, and nothing else, so only those
// three are passed on (`tags`, from visitorTags below). A tagged link with no
// campaign gets the placement's.
//
// Windows has its own counted link, which forwards to the newest Windows
// installer and takes the same tags. `platform` is 'mac' or 'windows'.
const DOWNLOAD_URLS = {
  mac: 'https://prep.pixally.com/download',
  windows: 'https://prep.pixally.com/download/windows',
};
const PASSED_TAGS = ['utm_source', 'utm_medium', 'utm_campaign'];
export const downloadHref = (campaign, tags, platform = 'mac') => {
  const url = DOWNLOAD_URLS[platform];
  if (!tags) return `${url}?utm_source=pixally.com&utm_medium=website&utm_campaign=${campaign}`;
  const query = new URLSearchParams(tags);
  if (!query.get('utm_campaign')) query.set('utm_campaign', campaign);
  return `${url}?${query}`;
};

// The tags to pass on from the address the visitor arrived on (its query
// string), as a query string, or null when it has no utm_source.
export const visitorTags = (search) => {
  const from = new URLSearchParams(search);
  if (!from.get('utm_source')) return null;
  const tags = new URLSearchParams();
  PASSED_TAGS.forEach((key) => {
    const value = from.get(key);
    if (value) tags.set(key, value.slice(0, 120));
  });
  return tags.toString();
};

// Since 22 Oct 2026 the pages build their download links with prepDownloadHref
// below, and Prep's server reads more than the three tags: the visitor's
// original outside source (kept in the browser for 90 days where the pixel's
// rule allows), Meta's click id, the button (`entry`) and via=sent
// (lib/prepAttribution.js sets out the link and the rules). A visitor with no
// outside source still gets the placement's own tags, exactly as from
// downloadHref above.
//
// Each button's `entry`. Only [a-z0-9_-]. The campaigns above stay what the
// site's own tags carry; `entry` is what tells the buttons apart.
export const ENTRY = {
  prepPage: 'prep_page', // /prep, the download in the hero
  prepPageEnd: 'prep_page_end', // /prep, the download at the foot of the page
  getPrep: 'get_prep', // /get-prep, the download in the hero
  getPrepEnd: 'get_prep_end', // /get-prep, the download at the foot of the page
  getPrepSticky: 'get_prep_sticky', // /get-prep, the bar at the foot of a phone's screen
};

// `visit` is usePrepVisit's (components/PrepParts.jsx): { campaign, source,
// via, ref, test }. `campaign` is already the _sent one on a page opened from
// a sent link.
export const prepDownloadHref = (platform, visit, entry) =>
  attributedDownloadHref(DOWNLOAD_URLS[platform], { ...visit, entry });
export const prepSendLink = (route, visit) => attributedSendLink(`https://pixally.com${route}`, visit);

// A phone or a tablet cannot run Prep, so where the download would be it gets a
// way to send this page to the computer that can: the device's own share sheet,
// an email to oneself, or the link copied. Nothing is collected and nothing is
// sent by Pixally; the visitor's own apps do the sending.
//
// The link carries ?via=sent so a download that began on another device can be
// counted on its own: a computer that opens it downloads under SENT_CAMPAIGN. It is a
// plain marker, not a UTM tag, so it does not rewrite where analytics says the
// visit came from. A visit that came on a tagged link sends those tags along
// too (prepSendLink above, with Meta's click id where advertising is on), so
// the download on the Mac is credited to the ad.
export const CAMPAIGN = 'prep_page';
export const SENT_CAMPAIGN = 'prep_page_sent';
export const SEND = {
  title: 'Pixally Prep',
  text: 'Pixally Prep for Mac and Windows (beta). Open this link on your computer to download it.',
  subject: 'Pixally Prep for Mac and Windows (beta)',
};
export const sendLink = (route, tags) => `https://pixally.com${route}?via=sent${tags ? `&${tags}` : ''}`;
export const sendMailHref = (link) =>
  `mailto:?subject=${encodeURIComponent(SEND.subject)}&body=${encodeURIComponent(`${SEND.text}\n\n${link}`)}`;

export const UPDATED_AT = '2026-10-18';
export const SEO_TITLE = 'Pixally Prep: Wedding Footage Synced, Transcribed and Marked on Your Mac or PC';
export const SEO_DESCRIPTION =
  'Pixally Prep is an app for wedding filmmakers, for Mac and Windows (beta). It preps your highlight, with the candid moments pulled aside, and builds the first cut of your doc edit.';

export const PRICE = '$8.99';
export const REQUIREMENT = 'a Mac with Apple silicon (M1 or later) and macOS 14 or later';
export const WINDOWS_REQUIREMENT = '64-bit Windows 10 or 11';
// The beta's promise, in the app's and Prep's terms' own words.
export const WINDOWS_BETA_PROMISE = "If a wedding comes out with real problems, tell us and we'll give you the wedding back.";
const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

// What each computer needs, in full: the question that answers it (FAQ, needs)
// and the app's structured data (scripts/prerender.mjs) both read these.
export const SYSTEM = {
  mac: 'Apple silicon (M1 or later) and macOS 14 Sonoma or later',
  windows: 'Windows 10 or 11 (64-bit), and a processor with AVX2 (most made since 2013) or an NVIDIA graphics card. An NVIDIA card makes it faster',
  both: 'about 25 GB free while a wedding prepares, about 3 GB free on the footage drive for the project, and an internet connection while it prepares',
  operatingSystem: 'macOS 14 or later; Windows 10 or 11 (64-bit, beta)',
  processor: 'Mac: Apple silicon (M1 or later). Windows: a processor with AVX2, or an NVIDIA graphics card',
};

export const HERO = {
  // The line under the wordmark.
  chip: 'For wedding filmmakers, on Mac and Windows (beta)',
  headline: 'The tedious part, done for you.',
  lead: "Drop in a wedding's footage. This is what is waiting when you open your editor.",
  // The two main features, side by side above the download. `art` names a strip
  // in lib/prepArt.js and `icon` a lucide icon mapped where the page is drawn.
  // `id` is how /get-prep orders them (AD_PAGE.features); /prep shows them in
  // this order.
  features: [
    {
      id: 'highlight',
      icon: 'clapperboard',
      art: 'stripHighlight',
      kicker: 'Highlight prep',
      title: 'Start cutting right away',
      text: 'Everything synced, transcribed and marked, with the soundbites and candid moments pulled aside.',
    },
    {
      id: 'doc-edit',
      icon: 'scissors',
      art: 'stripDocEdit',
      kicker: 'Doc edit',
      title: 'The first cut, built for you',
      text: 'Cut between your angles, with the dialogue balanced and chapter titles in. You open it and polish.',
    },
  ],
};

// What the visitor's device gets where the download would be. A Mac's is also
// what the prerendered page shows, because a build cannot see the device: the
// Mac button first, its line, then the Windows one.
export const DOWNLOAD = {
  label: 'Download for Mac',
  windowsLabel: 'Download for Windows (beta)',
  free: 'Your first wedding is free',
  mac: `${PRICE} per wedding after that. Needs ${REQUIREMENT}.`,
  windows: `${PRICE} per wedding after that. Needs ${WINDOWS_REQUIREMENT}.`,
  // The beta's promise, on its own line under Windows' note.
  windowsBeta: `While Windows is in beta, ${lowerFirst(WINDOWS_BETA_PROMISE)}`,
  // Windows SmartScreen warns about a new publisher until enough people have installed it, though the
  // installer is signed as Pixally. Under every Windows download, in Prep's words (Ryan, 19 Oct 2026,
  // after a beta user on the Facebook ad said Windows security stopped them).
  windowsSmartScreen: 'Windows may say it protected your PC the first time. Click More info, then Run anyway.',
  intel: `Prep needs ${REQUIREMENT}.`,
  // A phone or tablet (see SEND above). Short enough for one line on a 320px
  // phone, as "Download for Mac" is: "Send the link to my Mac" ran to two lines
  // on every phone and pushed the button off-centre (Ryan, 14 Oct 2026). Since
  // Windows it is "Send to computer": "Send to my computer" ran to two lines at
  // 320, 375 and 390px (Ryan, 18 Oct 2026).
  send: 'Send to computer',
  sendSub: 'Prep runs on a Mac or PC',
  sendEmail: 'email it to yourself',
  sendCopy: 'copy the link',
  sendCopied: 'link copied',
  sendFailed: 'open pixally.com/prep on your computer',
  soon: 'Coming soon',
  soonNote: `Your first wedding is free. ${PRICE} per wedding after that.`,
  waitlist: 'Join the Pixally waitlist',
};

// Under the button. The middle one is Ryan's wording.
export const PROMISES = [
  'No need to upload your footage anywhere',
  'No subscription',
  'One free wedding per person',
];

// The three editing programs Prep writes a project for. The icons are each
// program's own (public/prep/), shown to say what the project opens in;
// TRADEMARKS below names their owners. `label` is what the row shows: Final Cut
// Pro runs only on a Mac, so the row says so.
export const EDITORS = {
  label: 'Opens in',
  list: [
    { name: 'Premiere Pro', label: 'Premiere Pro', icon: '/prep/premiere-pro.png' },
    { name: 'DaVinci Resolve', label: 'DaVinci Resolve', icon: '/prep/davinci-resolve.png' },
    { name: 'Final Cut Pro', label: 'Final Cut Pro (Mac)', icon: '/prep/final-cut-pro.png' },
  ],
};
// The download buttons carry the Mac's and Windows' marks (lib/platformIcons.js).
export const TRADEMARKS =
  'Premiere Pro is a trademark of Adobe. DaVinci Resolve is a trademark of Blackmagic Design. Final Cut Pro, Mac and the Apple logo are trademarks of Apple. Windows and the Windows logo are trademarks of Microsoft. Pixally Prep is not affiliated with or endorsed by them.';

// The doc edit panel's drawing (lib/prepArt.js, firstCut) and its caption.
export const FIRST_CUT_CAPTION =
  'An illustration of the first cut Prep builds: cut between your angles, the sound in full, markers and chapter titles in place.';

// The doc edit's panel. The six doc edits and what a first cut holds are the
// app's own words (help.ts, "Which films can I make" and "What's in the first
// cut?").
export const DOC_EDIT = {
  heading: 'Everyone hates cutting the doc edit. Prep does it for you.',
  text: 'Choose the doc edits you deliver, and Prep builds the first cut of each one right in your project: cut between the angles, with the dialogue balanced, the markers on, and a chapter title opening each part of the day. You open it and polish.',
  listLabel: 'A first cut for each doc edit you choose',
  list: ['Full day', 'Ceremony + reception', 'Ceremony', 'Reception', 'Speeches', 'Dances'],
};

// The highlight's panel, the first of the two. Prep does not cut a highlight
// film, and the first sentence says so. Each
// card is one of the timelines Prep writes, under its real name, described in the
// app's own words (help.ts, "Which timelines do I get?" and "What are candid
// moments?"). The soundbite and candid timelines appear when Prep finds any.
export const HIGHLIGHT = {
  heading: 'Start cutting your highlight right away.',
  text: 'Prep does not edit your highlight film. It does the tedious part before it, so you open the project and get on with the creative part.',
  caption: 'An illustration of the candid moments timeline: each thing someone said, with the exchange around it and the camera that heard it.',
  list: [
    {
      timeline: 'Candid moments (camera sound)',
      title: 'Candid moments, pulled aside',
      text: 'Things people said that only the cameras heard: getting ready, cocktail hour, the party. Each one is picked out for you, with the exchange around it, ready for your highlight.',
    },
    {
      timeline: 'Highlight - soundbites',
      title: 'The lines worth using',
      text: 'Soundbites for your highlight. Each one is a whole sentence, with every angle under it.',
    },
    {
      timeline: 'Full Day - Rough Edit',
      title: 'The whole day, in order',
      text: 'The whole day laid out and roughly cut, to find your moments in. It is not your film. It is where you start building it.',
    },
  ],
};

// `art` names a drawing in lib/prepArt.js; the last card shows the editors instead.
export const FEATURES = {
  heading: 'And the prep is done before you open your editor',
  list: [
    {
      art: 'synced',
      title: 'Every camera and recorder, synced',
      text: 'For each part of the day you get a timeline with every camera and audio recorder lined up and stacked, nothing cut.',
    },
    {
      art: 'transcribed',
      title: 'Every word, transcribed',
      text: 'Prep transcribes the speech in your footage and writes a subtitle file for each timeline.',
    },
    {
      art: 'marked',
      title: 'The moments, marked',
      text: "Markers sit on the day's big moments, like the vows, the rings and each toast, and on the laughs, applause and tears.",
    },
    {
      art: null,
      title: 'A project your editor opens',
      text: 'Prep writes the project for Premiere Pro, DaVinci Resolve or, on a Mac, Final Cut Pro. Its timelines point at your files where they are.',
    },
  ],
};

export const PRICING = {
  heading: `${PRICE} per wedding`,
  lines: [
    'Your first wedding is free: one per person, and one per computer.',
    'Buy weddings in packs of 1, 5 or 10. There is no subscription.',
    'A wedding uses a credit only when it finishes, and a run that fails gives the credit back.',
  ],
};

// `id` is how /get-prep picks its questions (AD_PAGE.faq); /prep shows them all.
export const FAQ = [
  {
    id: 'first-cut',
    q: 'What is a doc edit first cut?',
    a: 'A first pass of the edit to start from: cut between the angles, with the dialogue balanced, the markers on, and each part of the day opening with its chapter title. The sound always plays in full, in real time.',
  },
  {
    id: 'doc-edits',
    q: 'Which doc edits does Pixally Prep cut?',
    a: 'Full day, Ceremony + reception, Ceremony, Reception, Speeches and Dances. Each one you choose gets a first cut built in your project.',
  },
  {
    id: 'highlight',
    q: 'Does Pixally Prep edit a highlight film?',
    a: 'No. A highlight film gets the prep only: every angle synced and transcribed, the moments marked, the soundbites and candid moments pulled aside, and the whole day laid out in order to pull from. You edit the film, and you can start right away.',
  },
  {
    id: 'candid',
    q: 'What are candid moments?',
    a: 'Things people said that only the cameras heard, like getting ready, cocktail hour and the party, picked out for you. When Prep finds them, each one is on the "Candid moments (camera sound)" timeline with the exchange around it.',
  },
  {
    id: 'editors',
    q: 'Which editors does Pixally Prep work with?',
    a: 'Prep writes a project for Premiere Pro or DaVinci Resolve, on a Mac or a PC, and for Final Cut Pro on a Mac. Final Cut Pro does not run on Windows.',
  },
  {
    id: 'upload',
    q: 'Do I have to upload my footage?',
    a: 'No. There is no need to upload your footage anywhere. Your video and audio files stay on your computer.',
  },
  {
    id: 'time',
    q: 'How long does Pixally Prep take?',
    a: 'It depends on how much footage there is and on your computer. As a guide on a Mac, allow about 45 seconds for each GB of footage, which is about 2 hours for 160 GB. On Windows (beta), an NVIDIA graphics card makes it faster.',
  },
  {
    id: 'windows',
    q: 'Does Pixally Prep run on Windows?',
    a: `Yes, as a beta, on ${WINDOWS_REQUIREMENT}, with projects for Premiere Pro and DaVinci Resolve. While it is a beta, ${lowerFirst(WINDOWS_BETA_PROMISE)}`,
  },
  {
    id: 'needs',
    q: 'What does my computer need?',
    a: `On a Mac: ${SYSTEM.mac}. On Windows (beta): ${SYSTEM.windows}. On either: ${SYSTEM.both}.`,
  },
  {
    id: 'intel',
    q: 'Does Pixally Prep run on an Intel Mac?',
    a: 'No. Prep needs Apple silicon, which means a Mac with an M1 chip or later.',
  },
  {
    id: 'cost',
    q: 'What does Pixally Prep cost after the free wedding?',
    a: `${PRICE} per wedding, bought in packs of 1, 5 or 10. There is no subscription, and a wedding uses a credit only when it finishes.`,
  },
];

export const GET_PREP = { heading: 'Get your next doc edit cut for you' };

// What Pixally does with the film next. The promotion's numbers come from
// config/pricingPromo.js. Joining the waitlist does not reserve the rate, and the
// copy does not say it does.
export const PIXALLY_NEXT = {
  heading: 'Pixally takes it from here',
  text:
    'Pixally is an all-in-one CRM for wedding photographers and videographers, with photo, video and hybrid gallery delivery built in. ' +
    `It opens on ${LAUNCH_LABEL}, and the first ${pricingPromo.promoMechanic.cap} accounts get the ${pricingPromo.promoLabel}: ${pricingPromo.discountPercent}% off the ${pricingPromo.discountDuration}. ` +
    'Join the waitlist to hear the moment it opens.',
  cta: 'Join the waitlist',
};

// THE CONTEST'S BAND. It takes the place of PIXALLY_NEXT only while the launch
// mode is CONTEST, like every contest surface on the site: nothing public may
// mention the prize until the sweepstakes is registered and the mode is flipped
// in the admin console (pages/PreLaunchPage.jsx says why). In PRELAUNCH, and in
// the prerendered page always, the band above is what is drawn.
//
// The prize's name, parts and picture are material terms of the sweepstakes.
// They repeat PRIZE_CAMERA in pages/PreLaunchPage.jsx, which must match Section
// 7 of the Official Rules, and scripts/prerender.mjs fails the build if this
// copy and that one differ. The rewards named are the ones the rules list
// (section 5): lifetime discounts, no swag; the second prize includes the
// Founding Partners' prizes (section 7).
export const CONTEST = {
  kicker: 'The Pixally waitlist',
  heading: 'Get in early. You could win this camera kit.',
  text:
    'Pixally is an all-in-one CRM for wedding photographers and videographers, with photo, video and hybrid gallery delivery built in. ' +
    `It opens on ${LAUNCH_LABEL}. Join the waitlist for an early look at what we are building, then climb the ladder for a bigger lifetime discount and a real shot at the grand prize.`,
  cta: 'Join the waitlist',
  prize: {
    tag: 'The grand prize',
    name: 'Sony FX5 Cinema Line',
    detail: 'Body, XLR handle unit, and a 24-70mm f/2.8 lens.',
    img: '/prize-sony-fx5-kit.webp',
  },
  disclaimer:
    'Prize is the Sony FX5 body, XLR handle unit, and 24-70mm f/2.8 lens. Props and any other accessories shown are for illustration and not included. Open to legal residents of the 50 U.S. & D.C. only. No purchase, payment, or referral necessary to enter or win.',
  rules: 'See Official Rules',
};

// The same facts as /about (content/compare/about.js).
export const BUILT_BY =
  'Built by Ryan Geldermann, a wedding filmmaker who has been filming weddings since 2009 and founded the wedding video editing company Uncut Gems.';

// Prep's own privacy policy and terms, which cover the app and its account. The
// site's Privacy Policy covers these pages.
export const PREP_LEGAL = [
  { id: 'prep-privacy', href: 'https://prep.pixally.com/privacy', label: 'Pixally Prep Privacy Policy' },
  { id: 'prep-terms', href: 'https://prep.pixally.com/terms', label: 'Pixally Prep Terms' },
];

// /get-prep: the page Prep's ads link to (Ryan, 14 Oct 2026). Most of its
// visitors arrive on a phone from Instagram or Facebook, where Prep cannot be
// downloaded, so the page is built for a phone first: the promise, then
// straight away the button that sends the link to their Mac, and a bar that
// keeps that button in reach. It has one job, so it has no site navigation and
// no waitlist band, and it is noindex: /prep is the page search finds.
//
// The words are /prep's, from the constants above, except the headline, the
// price line and the lead. The headline leads with the price (Ryan, 26 Oct
// 2026: "the $8.99 price needs to be much more prominent. get your doc edit
// edited for you for $8.99"). It says "cut", not "edited": Prep builds a first
// cut to polish, as the copy rules above put it, and "cut for you" is the
// phrase /prep already uses. The line under it says the price is per wedding
// and the first wedding is free, so the price is never read as the whole story.
// The lead names the two main features, the doc edit first to follow the
// headline, and the page shows them in that order too: the doc edit's card left
// of the highlight's, and its panel first (Ryan, 27 Oct 2026: "swap these two
// cards in both spots so the doc edit is left/first"). /prep keeps highlight
// prep first.
export const AD_ROUTE = '/get-prep';
export const AD_CAMPAIGN = 'get_prep';
export const AD_SENT_CAMPAIGN = 'get_prep_sent';
export const AD_PAGE = {
  // Drawn as: headline, then "for $8.99." with the price set large.
  headline: 'Get your doc edit cut for you',
  priceNote: 'Per wedding. Your first wedding is free.',
  lead: "Drop in a wedding's footage. Prep builds the first cut of your doc edit and gets your highlight ready to cut.",
  // HERO.features ids, in the order shown: the doc edit first.
  features: ['doc-edit', 'highlight'],
  // FAQ ids, in the order shown: the questions a filmmaker seeing Prep for the
  // first time asks before trying it.
  faq: ['upload', 'editors', 'first-cut', 'highlight', 'time', 'windows', 'needs', 'intel'],
};
